import React, { lazy, Suspense } from 'react';
import { Controller, useFormState } from 'react-hook-form';

import type { FieldValues, FieldPath, RegisterOptions, Control } from 'react-hook-form';
import type { IAllProps } from '@tinymce/tinymce-react';

// Lazy load TinyMCE Editor with proper typing
// Editor is a class component, so we cast it to React.ComponentType
const Editor = lazy<React.ComponentType<IAllProps>>(() =>
  import('@tinymce/tinymce-react').then((module) => ({
    default: module.Editor as React.ComponentType<IAllProps>,
  })),
);

type LazyRichTextEditorProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  defaultValue?: string;
  rules?: RegisterOptions<TFieldValues, TName>;
};

export const LazyRichTextEditor = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  defaultValue,
  rules = {},
}: LazyRichTextEditorProps<TFieldValues, TName>) => {
  const { isSubmitted } = useFormState({ control });

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue as any}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error, isTouched } }) => {
        const showError = (isTouched || isSubmitted) && error;
        return (
          <div className="w-full">
            <Suspense
              fallback={
                <div className="border border-gray-300 rounded p-4 bg-gray-50 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              }
            >
              <Editor
                licenseKey="gpl"
                tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                value={value}
                onEditorChange={onChange}
                init={{
                  height: 400,
                  menubar: false,
                  images_upload_handler: (blobInfo, progress) => {
                    console.log(blobInfo, progress);
                    return new Promise((resolve, reject) => {
                      resolve('https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png');
                    });
                  },
                  plugins: [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                    'preview',
                    'help',
                    'wordcount',
                  ],
                  toolbar:
                    'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | image | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                }}
              />
            </Suspense>
            {showError && <p className="text-red-500 text-sm mt-1">{error.message}</p>}
          </div>
        );
      }}
    />
  );
};
